import { Typography } from '@mui/material';
import { type GridApiPremium, type GridExceljsProcessInput, useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { now } from '@vy/date';
import type { Workbook } from 'exceljs';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo } from 'react';
import { type FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useGridColumnSettings } from '../../../../hooks/use-grid-column-settings';
import { useFiltersParam } from '../../../../hooks/search-params/use-filters-param';
import { useCustomerMasterReport } from '../../../../hooks/requests/use-customer-master-report';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { formatDate } from '../../../../utils/date';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { ReportTable } from '../../reports-components/ReportTable';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { flattenCustomerMasterForExport } from '../customer-master-report-hooks/flatten-customer-master-for-export';
import { useCustomerMasterFilterFields } from '../customer-master-report-hooks/use-customer-master-filter-fields';
import { useGetCustomerMasterReportColumns } from '../customer-master-report-hooks/use-get-customer-master-report-columns';
import { applyCustomerMasterFilters } from '../utils/apply-customer-master-filters';
import { buildCustomerMasterExport } from '../utils/build-customer-master-export';
import { CustomerMasterReportTable } from './customer-master-report-page-table/CustomerMasterReportTable';
import { CustomerMasterReportToolbar } from './CustomerMasterReportToolbar';
import { isNullOrUndefined } from '@vy/utils';
import type { IntegrationProviders } from '@vy/types';
import { useConnectedIntegrations } from '../../../../hooks/requests/use-integrations';

const REPORT_TITLE = 'Customers & Contracts Report';
const FILTERS_PARAM_NAME = 'customerMasterFilters';

export const CustomerMasterReportContent: React.FC = () => {
  const { data: customerMasterReportData, isLoading } = useCustomerMasterReport();
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const apiRef = useGridApiRef();
  const { data: connectedIntegrations } = useConnectedIntegrations();
  const connectedProviders = useMemo<ReadonlySet<IntegrationProviders>>(
    () => new Set(connectedIntegrations ?? []),
    [connectedIntegrations],
  );

  const { report } = customerMasterReportData;
  const filterFields = useCustomerMasterFilterFields(report);
  const [rawFilters, setRawFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = useMemo(() => rawFilters ?? [], [rawFilters]);

  const filteredReport = useMemo(() => applyCustomerMasterFilters(report, filters), [report, filters]);

  const columns = useGetCustomerMasterReportColumns(filteredReport, connectedProviders);

  const { openSettings, closeSettings, columnSettings, handleColumnVisibilityChange } = useGridColumnSettings(
    apiRef as MutableRefObject<GridApiCommunity>,
    columns,
  );

  const handleExport = useCallback(async () => {
    const api = apiRef.current;

    if (isNullOrUndefined(api)) {
      return;
    }

    const fileName = `${REPORT_TITLE} (issued ${formatDate(now())})`;
    const rows = flattenCustomerMasterForExport(filteredReport, connectedProviders);

    await toastFromPromise(
      api.exportDataAsExcel({
        fileName,
        exceljsPostProcess: async ({ workbook }: GridExceljsProcessInput) => {
          buildCustomerMasterExport(workbook as unknown as Workbook, { rows });
        },
      }),
      {
        pending: 'Downloading...',
        success: 'Download complete',
        error: 'Failed to download',
      },
    );
  }, [apiRef, filteredReport, connectedProviders]);

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          fontSize: '20px',
          mb: 1,
        }}
      >
        {REPORT_TITLE}
      </Typography>
      <ReportTable
        title={REPORT_TITLE}
        hideTitle
        hideMenu
        exportCsvFileName={REPORT_TITLE}
        reportCreationDate={customerMasterReportData.creationDate}
        reportType={REPORT_TYPES.CUSTOMER_MASTER}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        onExportCsv={handleExport}
      >
        <CustomerMasterReportToolbar
          onSearchChange={setSearchText}
          searchValue={searchText ?? ''}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={setRawFilters}
          columnSettings={columnSettings}
          onColumnSettingsOpen={openSettings}
          onColumnSettingsClose={closeSettings}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onExport={handleExport}
        />

        <CustomerMasterReportTable
          report={filteredReport}
          columns={columns}
          searchTerm={searchText ?? undefined}
          apiRef={apiRef as MutableRefObject<GridApiPremium>}
          isLoading={isLoading}
        />
      </ReportTable>
    </>
  );
};
